
import React from 'react';
import {
  Cpu, User, Info, CheckCircle2, Zap, BrainCircuit,
  TrendingUp, Wallet, Target, Globe, GraduationCap,
  Briefcase, Heart, Lightbulb, MapPin, Search, ArrowUpRight,
  Activity, Users, BarChart3, ShieldCheck, Loader2
} from 'lucide-react';


// Map common titles to icons
const getIconForTitle = (title: string) => {
  const t = (title || '').toLowerCase();
  if (t.includes('schaarste') || t.includes('scarcity')) return <Activity />;
  if (t.includes('salaris') || t.includes('salary') || t.includes('bench')) return <Wallet />;
  if (t.includes('motivatie') || t.includes('drijfveer')) return <Heart />;
  if (t.includes('source') || t.includes('sourcing') || t.includes('mix')) return <Target />;
  if (t.includes('opleiding') || t.includes('education')) return <GraduationCap />;
  if (t.includes('senioriteit') || t.includes('experience')) return <Briefcase />;
  if (t.includes('skill')) return <Lightbulb />;
  if (t.includes('tech') || t.includes('tool')) return <Cpu />;
  if (t.includes('reach') || t.includes('location')) return <MapPin />;
  if (t.includes('flow')) return <Users />;
  return <BarChart3 />;
};

const getColorForTitle = (title: string) => {
  const t = (title || '').toLowerCase();
  if (t.includes('schaarste')) return 'bg-rose-50 text-rose-600';
  if (t.includes('salaris')) return 'bg-emerald-50 text-emerald-600';
  if (t.includes('motivatie')) return 'bg-red-50 text-red-600';
  if (t.includes('source')) return 'bg-red- text-red-';
  if (t.includes('opleiding')) return 'bg-purple-50 text-purple-600';
  if (t.includes('senioriteit')) return 'bg-orange-50 text-orange-600';
  if (t.includes('skill')) return 'bg-amber-50 text-amber-600';
  if (t.includes('tech')) return 'bg-cyan-50 text-cyan-600';
  if (t.includes('reach')) return 'bg-teal-50 text-teal-600';
  return 'bg-slate-100 text-slate-600';
};

const InsightCard: React.FC<{ icon?: React.ReactNode, title: string, value: string, color?: string, trend?: string, subtitle?: string }> = ({ icon, title, value, color, trend, subtitle }) => {
  const iconElement = icon || getIconForTitle(title);
  const colorClass = color || getColorForTitle(title);

  return (
    <div className={`bg-white p-4 rounded-md border border-slate-200 shadow-sm hover:border-slate-300 transition-all flex flex-col gap-3 group relative overflow-hidden`}>
      <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${colorClass} transition-transform group-hover:rotate-6 shadow-sm`}>
        {React.isValidElement(iconElement) ? React.cloneElement(iconElement as React.ReactElement<any>, { size: 16 }) : <BarChart3 size={16} />}
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{title}</h4>
          {trend && <span className="text-[9px] font-bold text-emerald-600 flex items-center gap-0.5"><ArrowUpRight size={10} /> {trend}</span>}
        </div>
        <p className="text-xs font-bold text-slate-900 leading-snug">{value}</p>
        {subtitle && <p className="text-[9px] text-slate-400 mt-0.5 font-medium italic truncate">{subtitle}</p>}
      </div>
    </div>
  );
};

interface MarketResearchOverviewProps {
  aiInsights?: string;
  customInsights?: any[];
  isLoading?: boolean;
}

const MarketResearchOverview: React.FC<MarketResearchOverviewProps> = ({ aiInsights, customInsights, isLoading = false }) => {
  // If loading, show skeleton state
  if (isLoading) {
    return (
      <div className="space-y-8 animate-in fade-in duration-700 opacity-50 pointer-events-none">
        <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-slate-200 rounded-[2rem]">
          <Loader2 className="animate-spin text-[#e51b23] mb-4" size={32} />
          <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Aan het analyseren...</p>
        </div>
      </div>
    );
  }

  const hasData = (customInsights && customInsights.length > 0) || !!aiInsights;

  // If no data and not loading, show empty state
  if (!hasData) {
    return (
      <div className="h-[60vh] flex flex-col items-center justify-center text-center border-2 border-dashed border-slate-200 rounded-[2rem] bg-slate-50/50">
        <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-sm mb-6">
          <Zap className="text-slate-300" size={32} />
        </div>
        <h3 className="text-slate-900 font-black text-xl mb-2 uppercase tracking-tight">Nog geen analyse</h3>
        <p className="text-slate-500 text-sm font-medium max-w-md mx-auto mb-8">
          Start de Froukje AI Motor om de arbeidsmarkt te scannen voor deze vacature.
        </p>
        {/* Button is in sidebar, so just instruction here */}
      </div>
    );
  }

  const displayInsights = customInsights || [];

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-slate-900 tracking-tight uppercase">10-Point Intelligence</h3>
          <span className="text-[9px] font-bold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-sm uppercase tracking-widest border border-slate-200">
            {customInsights && customInsights.length > 0 ? 'AI Verified' : 'Standard'}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {displayInsights.map((insight, idx) => (
            <InsightCard key={idx} {...insight} />
          ))}
        </div>
      </section>

      {aiInsights && (
        <div className="bg-[#000000] rounded-md p-6 text-white shadow-md relative overflow-hidden border border-slate-800">
          <div className="relative z-10 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-red-600/20 border border-red-500/20 rounded-sm backdrop-blur-xl flex items-center justify-center shrink-0">
                <BrainCircuit size={16} className="text-red-400" />
              </div>
              <h3 className="text-sm font-bold uppercase tracking-tight">Froukje's Diepte Analyse</h3>
            </div>
            <div className="prose prose-invert prose-sm max-w-none opacity-80 leading-relaxed font-medium whitespace-pre-wrap text-xs">
              {aiInsights}
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default MarketResearchOverview;
